import { supabase } from '@/lib/supabase';
import { slotKeyOf, type SlotUtc } from './bookingShared';

/**
 * Las tres llamadas de agendamiento, pidiendo INSTANTES y cayendo al reloj de
 * pared si la base todavía no los ofrece.
 * ============================================================================
 *
 * `20260826120000` agregó `fn_compute_availability_utc`, `fn_slot_occupancy_utc`
 * y `fn_appointment_book_utc`. Son funciones NUEVAS: las de siempre siguen
 * intactas y devolviendo exactamente lo mismo que hoy.
 *
 * Por qué el fallback y no simplemente llamar a las nuevas: este repo despliega
 * a producción GLOBAL: el sitio de todos los clientes publicados a la vez. Si el
 * deploy saliera antes que la migración —o si hubiera que revertirla— cada sitio
 * quedaría sin poder mostrar horarios. Con el fallback, el orden entre deploy y
 * migración deja de importar.
 *
 * El fallback es por error de la RPC, no por versión ni por flag: si
 * `fn_compute_availability_utc` no existe, PostgREST responde PGRST202 y se
 * reintenta con la vieja. Cuando la migración está puesta, la primera llamada
 * acierta y no hay segundo viaje.
 */

export type Disponibilidad = {
  slots: SlotUtc[];
  /** true = los cupos traen instante real; false = reloj de pared, como siempre. */
  utc: boolean;
};

/**
 * Memoria corta (60 s) de la disponibilidad por sucursal y día, compartida
 * por todos los que la piden en la misma página.
 *
 * En la ficha la misma pregunta se hacía dos veces: el chequeo de "¿hay cupo
 * en 30 días?" que decide el botón Agendar, y el calendario del modal de
 * agenda, que al montarse carga su mes. Medido: 36 llamadas para 31 días
 * distintos (61 para 43 en tenants sin agenda, por el fallback). Con esto
 * cada (sucursal, día) viaja una vez; una llamada en curso se comparte y un
 * fallo no se guarda, así el siguiente que pregunte reintenta. 60 s porque
 * los cupos (horario configurado) no cambian en el rato de una visita; la
 * OCUPACIÓN (`loadOccupancy`) no pasa por acá y sigue consultándose siempre.
 */
const MEMORIA_MS = 60_000;
const memoria = new Map<string, { t: number; promise: Promise<Disponibilidad> }>();

export interface OpcionesDisponibilidad {
  /**
   * Saltear la memoria y consultar la base. Lo usa el paso en que el visitante
   * ELIGE la hora (BookingModal/AgendarForm): ahí una lista de cupos de hasta
   * 60 s atrás podría ofrecer una hora que la automotora acaba de cerrar, y
   * tras un `slot_not_available` el reintento volvería a mostrar lo mismo. Los
   * chequeos por día (botón Agendar, calendario del mes) sí usan la memoria.
   */
  sinMemoria?: boolean;
}

export function loadAvailability(
  clientId: number, dealershipId: number, ymd: string, tz: string,
  opciones?: OpcionesDisponibilidad,
): Promise<Disponibilidad> {
  const clave = `${clientId}:${dealershipId}:${ymd}:${tz}`;
  const ahora = Date.now();
  const guardada = memoria.get(clave);
  if (!opciones?.sinMemoria && guardada && ahora - guardada.t < MEMORIA_MS) return guardada.promise;
  const promise = loadAvailabilityDeLaBase(clientId, dealershipId, ymd, tz).catch((e) => {
    if (memoria.get(clave)?.promise === promise) memoria.delete(clave);
    throw e;
  });
  memoria.set(clave, { t: ahora, promise });
  return promise;
}

async function loadAvailabilityDeLaBase(
  clientId: number, dealershipId: number, ymd: string, tz: string,
): Promise<Disponibilidad> {
  const utc = await supabase.rpc('fn_compute_availability_utc', {
    p_client_id: clientId, p_dealership_id: dealershipId, p_date: ymd,
  });

  if (!utc.error) {
    const filas = (utc.data || []) as Array<{
      slot_start_utc: string; slot_end_utc: string; capacity: number; timezone: string;
    }>;
    return {
      utc: true,
      slots: filas.map((s) => ({
        // `slot_start` se conserva como identidad del cupo —es lo que comparan
        // los componentes para saber cuál está elegido— pero ya es el instante.
        slot_start: s.slot_start_utc,
        slot_end: s.slot_end_utc,
        slot_start_utc: s.slot_start_utc,
        slot_end_utc: s.slot_end_utc,
        tz: s.timezone || tz,
        capacity: Number(s.capacity) || 1,
      })),
    };
  }

  const vieja = await supabase.rpc('fn_compute_availability', {
    p_client_id: clientId, p_dealership_id: dealershipId, p_date: ymd, p_timezone: tz,
  });
  if (vieja.error) throw vieja.error;
  return { utc: false, slots: (vieja.data || []) as SlotUtc[] };
}

/** Cupos ya tomados, indexados con la misma clave que devuelve `slotKeyOf`. */
export async function loadOccupancy(
  clientId: number, dealershipId: number, ymd: string, tz: string, utc: boolean,
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};

  if (utc) {
    const r = await supabase.rpc('fn_slot_occupancy_utc', {
      p_client_id: clientId, p_dealership_id: dealershipId, p_date: ymd,
    });
    if (r.error) { console.warn('Ocupación (utc):', r.error.message); return counts; }
    for (const row of (r.data || []) as Array<{ slot_start_utc: string; taken: number }>) {
      const key = slotKeyOf(row); if (!key) continue;
      counts[key] = Number(row.taken) || 0;
    }
    return counts;
  }

  const r = await supabase.rpc('fn_slot_occupancy', {
    p_client_id: clientId, p_dealership_id: dealershipId, p_date: ymd, p_timezone: tz,
  });
  if (r.error) { console.warn('Ocupación:', r.error.message); return counts; }
  for (const row of (r.data || []) as Array<{ slot_start: string; taken: number }>) {
    const key = slotKeyOf(row); if (!key) continue;
    counts[key] = Number(row.taken) || 0;
  }
  return counts;
}

export type DatosReserva = {
  clientId: number;
  dealershipId: number;
  serviceName: string | null;
  slot: SlotUtc;
  /** Fecha LOCAL de la sucursal. Sólo la usa el camino viejo. */
  ymd: string;
  tz: string;
  vehicleId?: number | null;
  customerId?: number | null;
  channel?: string;
  notes?: string | null;
  contact: Record<string, unknown>;
};

/**
 * Reserva. Con instante va por `fn_appointment_book_utc`, que convierte y
 * DELEGA en `fn_appointment_book`: mismo writer, mismas guardas y —lo que
 * importa para el front— los MISMOS códigos de error (`slot_taken`,
 * `slot_in_past`, `already_booked`, `too_many_appointments`…), así que los
 * mensajes traducidos que ya existen siguen sirviendo igual.
 *
 * El front NO escribe `slot_start_utc`: lo llena el trigger del paso 1.
 */
export async function bookSlot(d: DatosReserva) {
  if (d.slot.slot_start_utc && d.slot.slot_end_utc) {
    return supabase.rpc('fn_appointment_book_utc', {
      p_client_id: d.clientId,
      p_dealership_id: d.dealershipId,
      p_service_name: d.serviceName,
      p_slot_start_utc: d.slot.slot_start_utc,
      p_slot_end_utc: d.slot.slot_end_utc,
      p_vehicle_id: d.vehicleId ?? null,
      p_customer_id: d.customerId ?? null,
      p_channel: d.channel || 'web',
      p_notes: d.notes ?? null,
      p_contact_snapshot: d.contact,
    } as never);
  }

  return supabase.rpc('fn_appointment_book', {
    p_client_id: d.clientId,
    p_dealership_id: d.dealershipId,
    p_service_name: d.serviceName,
    p_slot_start: d.slot.slot_start,
    p_slot_end: d.slot.slot_end,
    p_date: d.ymd,
    p_timezone: d.tz,
    p_vehicle_id: d.vehicleId ?? null,
    p_customer_id: d.customerId ?? null,
    p_channel: d.channel || 'web',
    p_notes: d.notes ?? null,
    p_contact_snapshot: d.contact,
  } as never);
}
